import React, { useContext, useState } from "react";
import { useHistory } from "react-router-dom";

import { DictateButton, DictationOverlay } from "../../dictation";
import requestDictationSchema from "../constants/requestDictation";
import {
  createDictatedRequests,
  dictateRequest,
  redictateRequestText,
} from "../services/requestDictation";
import { UserContext } from "../../contexts";
import RequestDictationReview from "./RequestDictationReview.jsx";

/**
 * The request-specific wiring around the shared dictation overlay: the schema,
 * the editable review, and what happens once the rep confirms.
 *
 * Sibling of ListingDictation. The overlay itself is untouched — recording, the
 * start/stop cues, the Analysing sweep and the editable-and-re-analysed
 * transcript all come from there.
 *
 * ⚠️ CREATION IS NOT WIRED YET. `onCreate` is deliberately a no-op that reports
 * itself: the sheet can be used end to end for reviewing what was heard, but
 * pressing the button creates nothing. A part exchange is up to seven writes
 * with no rollback, and it has to go through the request provider's own
 * functions rather than a parallel path — that is its own commit, with its own
 * tests for the partial-failure behaviour. Shipping a button that silently did
 * nothing would be worse than one that says so.
 */
const RequestDictation = ({ disabled = false, title }) => {
  const history = useHistory();
  const { user } = useContext(UserContext) ?? {};
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [created, setCreated] = useState(null);

  const upload = (blob, { signal }) => dictateRequest(blob, { signal });

  /**
   * Up to seven writes and NOT transactional — see createDictatedRequests. On a
   * failure part-way through, what already exists is named rather than rolled
   * back: deleting rows that exist is itself a write that can fail, and would
   * turn one bad state into two. The rep is pointed at the flow, which resumes a
   * partially-built deal from its group.
   */
  const create = async ({ client, intent, orders, sells }) => {
    setCreating(true);
    setCreateError(null);
    try {
      const made = await createDictatedRequests({ client, intent, orders, sells, user });
      // The sheet STAYS OPEN and reports what it made, like the listing one.
      // Navigating on success took the rep somewhere they had not asked to go,
      // and closed the sheet before they could read what happened.
      setCreated({ ...made, intent });
    } catch (e) {
      const made = e?.created;
      const existing = made
        ? [
            made.orderIds.length ? `${made.orderIds.length} order` : null,
            made.sellIds.length ? `${made.sellIds.length} sell` : null,
          ].filter(Boolean).join(" and ")
        : "";
      setCreateError(
        existing
          ? `${e.message} ${existing} request(s) were already created — open the deal in the flow and finish it there rather than dictating again.`
          : (e?.userFacing ? e.message : "Could not create the request. Nothing was saved.")
      );
    } finally {
      setCreating(false);
    }
  };

  const view = () => {
    const made = created;
    if (!made) return;
    const id = made.orderIds[0] ?? made.sellIds[0];
    setOpen(false);
    setCreated(null);
    if (id) {
      history.push(
        made.intent === "sell" ? `/request/sell-enquiry/${id}` : `/request/sourcing/${id}`
      );
    }
  };

  const close = () => {
    setOpen(false);
    setCreated(null);
    setCreateError(null);
  };

  return (
    <>
      <DictateButton
        onClick={() => setOpen(true)}
        disabled={disabled}
        label="Dictate a request"
        {...(title ? { title } : {})}
      />
      {open && (
        <DictationOverlay
          isOpen={open}
          onClose={close}
          schema={requestDictationSchema}
          upload={upload}
          // The correction path: a fixed transcript goes back through the same
          // endpoint as `text`, so the cards re-derive from what the rep
          // actually meant rather than disagreeing with the text above them.
          reanalyse={(text) => redictateRequestText(text)}
          renderReview={(result) => (
            <RequestDictationReview
              result={result}
              onCreate={create}
              creating={creating}
              createError={createError}
              created={created}
              onView={view}
            />
          )}
          onSearchManually={close}
        />
      )}
    </>
  );
};

export default RequestDictation;
